/**
 * PWA (spec docs/superpowers/specs/2026-09-24-pwa-design.md).
 * - 서비스 워커 등록(/sw.js, 범위 /) — 페이지를 연 뒤.
 * - 새 버전: 이미 관리되던 페이지에서 컨트롤러가 바뀌면 「새 버전이 있습니다 · 새로고침」 토스트(저절로 닫지도,
 *   새로고침하지도 않는다). 앱이 앞으로 올 때마다 업데이트 확인(60초에 한 번).
 * - 앱 아이콘 배지(syncAppBadge)와 설치 안내(Task 4)도 이 모듈이다 — 설치 안내 배너(#pwaBanner, 2026-09-30)도.
 * 문구는 #pwaLabels 의 data-* (없으면 한국어).
 */
import { openSheet, closeSheet } from './mobile-shell.js';
import { showNotification } from './notifications.js';
import { watchPwaUpdate } from './pwa-update.js';
import { isPhone } from './phone.js';
import { installCase, chromiumBrowser, installEntryHidden, relatedWebappInstalled } from './pwa-install-logic.js';

export { watchPwaUpdate };

const UPDATE_EVERY = 60 * 1000;
let wired = false;
let lastUpdate = 0;

function label(key, fallback) {
    const el = document.getElementById('pwaLabels');
    return (el && el.dataset[key]) || fallback;
}

// 브라우저 설치 창(beforeinstallprompt). index.html 머리의 인라인 스크립트가 이 모듈보다 먼저 받아 window.__pwaPrompt 에
// 둔다 — 모듈 60여 개가 다 로드되기 전에 지나간 이벤트도 놓치지 않게. 쓰고 나면 둘 다 비운다(크롬은 한 번만 준다).
let deferredPrompt = null;
// 이 브라우저에 이미 설치됐는가(getInstalledRelatedApps — 크롬 · 에지 140+ 데스크톱, 안드로이드 크롬 84+).
let installedHere = false;
const CHROME_STORE = 'https://play.google.com/store/apps/details?id=com.android.chrome';

function isStandalone() {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || navigator.standalone === true;
}

function currentPrompt() {
    return deferredPrompt || window.__pwaPrompt || null;
}

function takePrompt() {
    const p = currentPrompt();
    deferredPrompt = null;
    window.__pwaPrompt = null;
    return p;
}

// 삼성 인터넷은 설치 창(beforeinstallprompt)을 **쏘더라도** 크롬 안내가 먼저다 — 그 창이 바로
// 안드로이드 14+ 가 "이전 버전용 앱"·Play 프로텍트 경고를 띄우는 옛 SDK WebAPK 를 만든다 (spec §0.1).
// 판정 자체는 pwa-install-logic.js 의 installCase (단위 테스트).
export function installContext(ua = navigator.userAgent, nav = navigator) {
    const data = nav.userAgentData;
    return installCase({
        ua,
        brands: data && data.brands,
        mobile: !!(data && data.mobile),
        maxTouchPoints: nav.maxTouchPoints,
        standalone: isStandalone(),
        prompt: !!currentPrompt(),
        installed: installedHere,
    });
}

function chromeIntent(loc) {
    return 'intent://' + loc.host + loc.pathname + loc.search + '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=' + encodeURIComponent(CHROME_STORE) + ';end';
}

// 「앱 설치」 항목: 앱으로 실행 중이면 숨긴다. 아바타 메뉴(넓은 화면 — PC·아이패드·태블릿)는 설치할 길을 안내할 수
// 있을 때만 보인다 — 데스크톱 파이어폭스처럼 "브라우저 메뉴를 보세요"만 남는 경우나 이미 설치된 브라우저에서는 소음이다.
function syncInstallItems() {
    const ctx = installContext();
    document.querySelectorAll('[data-pwa-install]').forEach((el) => {
        const place = el.closest('#userMenuDropdown') ? 'menu' : el.closest('#mAllSheet') ? 'all' : 'other';
        el.hidden = installEntryHidden(ctx, place);
    });
}

function installSheet() {
    return document.getElementById('pwaInstallSheet');
}

// 모달 안을 지금 경우에 맞춘다 — 열 때, 그리고 열려 있는 동안 설치 창이 준비되거나 설치를 마쳤을 때.
function paintInstallSheet(sheet, ctx) {
    sheet.dataset.form = isPhone() ? 'phone' : 'desktop';
    sheet.dataset.case = ctx;
    const shown = ctx === 'prompt' ? (sheet.dataset.shownCase || 'other') : ctx;
    sheet.querySelectorAll('[data-pwa-case]').forEach((el) => { el.hidden = el.dataset.pwaCase !== shown; });
    // 주소창 그림의 아이콘은 <svg> 라 .hidden 속성이 없다 — 속성으로 건다
    const browser = chromiumBrowser(navigator.userAgent, navigator.userAgentData && navigator.userAgentData.brands) === 'edge' ? 'edge' : 'chrome';
    sheet.querySelectorAll('[data-pwa-browser]').forEach((el) => { el.toggleAttribute('hidden', el.dataset.pwaBrowser !== browser); });
    sheet.querySelectorAll('[data-pwa-host]').forEach((el) => { el.textContent = window.location.host; });
    const chrome = sheet.querySelector('a[data-pwa-chrome]');
    if (chrome) {
        chrome.href = chromeIntent(window.location);
        chrome.hidden = shown !== 'samsung';
    }
    const ready = ctx === 'prompt';
    const btn = sheet.querySelector('[data-pwa-prompt]');
    if (btn) btn.hidden = !ready;
    const status = sheet.querySelector('[data-pwa-ready]');
    if (status) status.textContent = ready ? label('ready', '설치할 준비가 됐습니다 — 「설치」를 누르세요') : '';
}

function openInstallSheet(opener) {
    const sheet = installSheet();
    if (!sheet) return;
    const ctx = installContext();
    sheet.dataset.shownCase = ctx;
    paintInstallSheet(sheet, ctx);
    // 「전체」 시트 안에서 열면 「전체」를 먼저 닫는다 — 그렇지 않으면 뒤 화면의 inert 가 「전체」와 함께 풀리고,
    // 닫은 뒤 포커스가 숨은 시트 안의 버튼으로 돌아간다.
    const all = opener && opener.closest('#mAllSheet');
    if (all && !all.hidden) {
        closeSheet(all);
        opener = document.getElementById('mAllBtn') || opener;
    }
    openSheet(sheet, { opener });
}

// 열려 있는 모달에서 설치 창이 준비되면 「설치」 버튼을 보인다(포커스는 옮기지 않는다 — 상태 줄이 읽는다).
function onPromptReady() {
    const sheet = installSheet();
    if (sheet && !sheet.hidden) paintInstallSheet(sheet, installContext());
}

function runPrompt() {
    const prompt = takePrompt();
    if (!prompt) return;
    prompt.prompt();
    Promise.resolve(prompt.userChoice).catch(() => {}).finally(() => { syncInstallItems(); onPromptReady(); });
}

// ── 설치 안내 배너 (2026-09-30) ──
// 설치할 길이 있는 브라우저(설치 창 · 삼성 인터넷 · 아이폰/아이패드)에서만, 틀 안의 탭이 아닐 때, 자동화 브라우저가
// 아닐 때(navigator.webdriver — e2e 스위트의 화면 아래를 가리지 않는다), 「나중에」로 미룬 30일이 지났을 때.
const BANNER_KEY = 'pwa.banner.until';
const BANNER_SESSION = 'pwa.banner.hidden';
const DAY = 24 * 60 * 60 * 1000;
function storageGet(store, key) { try { return window[store].getItem(key); } catch (_) { return null; } }
function storageSet(store, key, v) { try { window[store].setItem(key, v); } catch (_) { /* 저장소가 막힌 창 */ } }
function snoozeBanner(days) { storageSet('localStorage', BANNER_KEY, String(Date.now() + days * DAY)); }

export function shouldShowBanner({ ctx, webdriver, inFrame, snoozedUntil, now, hiddenThisSession }) {
    if (webdriver || inFrame || hiddenThisSession) return false;
    if (Number(snoozedUntil || 0) > now) return false;
    return ctx === 'prompt' || ctx === 'samsung' || ctx === 'ios';
}

function syncBanner() {
    const el = document.getElementById('pwaBanner');
    if (!el) return;
    const show = shouldShowBanner({
        ctx: installContext(),
        webdriver: navigator.webdriver === true,
        inFrame: !!document.documentElement.dataset.workspaceFrame,
        snoozedUntil: storageGet('localStorage', BANNER_KEY),
        now: Date.now(),
        hiddenThisSession: storageGet('sessionStorage', BANNER_SESSION) === '1',
    });
    if (show) {
        const phone = isPhone();
        el.querySelectorAll('[data-pwa-banner-body]').forEach((b) => { b.hidden = (b.dataset.pwaBannerBody === 'phone') !== phone; });
    }
    el.hidden = !show;
}

function onBannerClick(e) {
    const el = document.getElementById('pwaBanner');
    if (!el || !el.contains(e.target)) return;
    if (e.target.closest('[data-pwa-banner-later]')) {
        snoozeBanner(30);
        el.hidden = true;
    } else if (e.target.closest('[data-pwa-banner-install]')) {
        // 설치 창을 닫아도 이 창에서는 다시 조르지 않는다. 설치 창이 없는 기기(안내 시트)는 안내를 본 것으로 30일.
        storageSet('sessionStorage', BANNER_SESSION, '1');
        if (installContext() !== 'prompt') snoozeBanner(30);
        el.hidden = true;
    }
}

function onInstallClick(e) {
    const now = e.target.closest('[data-pwa-prompt]');
    if (now) {
        e.preventDefault();
        const sheet = installSheet();
        if (sheet && !sheet.hidden) closeSheet(sheet);
        runPrompt();
        return;
    }
    const item = e.target.closest('[data-pwa-install]');
    if (!item) return;
    e.preventDefault();
    if (installContext() === 'prompt') {
        runPrompt();
        return;
    }
    openInstallSheet(item);
}

window.addEventListener('beforeinstallprompt', (e) => { deferredPrompt = e; syncInstallItems(); syncBanner(); onPromptReady(); });
window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    window.__pwaPrompt = null;
    installedHere = true;
    snoozeBanner(365);
    syncInstallItems();
    syncBanner();
    const sheet = installSheet();
    if (sheet && !sheet.hidden) closeSheet(sheet);
    showNotification(label('installed', '앱을 설치했습니다'), 'success');
});

// 이 브라우저에 이미 설치됐는지 한 번 묻는다 — 매니페스트의 related_applications(serveManifest 가 요청 오리진으로 채운다).
function checkInstalledHere() {
    if (isStandalone() || typeof navigator.getInstalledRelatedApps !== 'function') return;
    navigator.getInstalledRelatedApps().then((apps) => {
        if (!relatedWebappInstalled(apps)) return;
        installedHere = true;
        syncInstallItems();
        syncBanner();
    }).catch(() => {});
}

function toastStack() {
    let stack = document.getElementById('notificationStack');
    if (!stack) {
        stack = document.createElement('div');
        stack.id = 'notificationStack';
        document.body.appendChild(stack);
    }
    return stack;
}

function showUpdateToast() {
    if (document.getElementById('pwaUpdateToast')) return;
    const el = document.createElement('div');
    el.id = 'pwaUpdateToast';
    el.className = 'notification notification-info show pwa-update-toast';
    el.setAttribute('role', 'status');
    const content = document.createElement('div');
    content.className = 'notification-content';
    const msg = document.createElement('span');
    msg.className = 'notification-message';
    msg.textContent = label('update', '새 버전이 있습니다');
    const reload = document.createElement('button');
    reload.type = 'button';
    reload.className = 'btn btn-primary btn-sm pwa-reload';
    reload.textContent = label('reload', '새로고침');
    reload.addEventListener('click', () => window.location.reload());
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'notification-close';
    close.setAttribute('aria-label', label('close', '닫기'));
    close.textContent = '×';
    close.addEventListener('click', () => el.remove());
    content.append(msg, reload, close);
    el.append(content);
    toastStack().append(el);
}

function register() {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
}

function checkForUpdate() {
    if (document.visibilityState !== 'visible' || Date.now() - lastUpdate < UPDATE_EVERY) return;
    lastUpdate = Date.now();
    navigator.serviceWorker.getRegistration('/').then((reg) => reg && reg.update()).catch(() => {});
}

let lastBadge = null;

export function syncAppBadge(n) {
    if (!('setAppBadge' in navigator)) return;
    // 대리 보기 중에는 남의 숫자를 내 앱 아이콘에 달지 않는다.
    if (document.querySelector('.impersonation-banner')) return;
    const count = Number(n) || 0;
    if (count === lastBadge) return; // 30초 폴링마다 같은 값을 다시 달지 않는다
    lastBadge = count;
    (count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge()).catch(() => {});
}

// 로그인하지 않은 화면(벨도 아바타 메뉴도 없음)은 앞사람의 숫자를 지운다.
function signedOut() {
    return !document.getElementById('notifBell') && !document.getElementById('userMenuDropdown');
}

export function setupPwa() {
    if (wired) return;
    wired = true;
    document.addEventListener('click', onBannerClick, true); // 「설치」의 설치 창보다 먼저 배너를 걷는다
    document.addEventListener('click', onInstallClick);
    ['calendar:navigated', 'calendar:page-swapped'].forEach((name) => document.addEventListener(name, syncInstallItems));
    document.addEventListener('click', (e) => { if (e.target.closest('#mAllBtn')) syncInstallItems(); }, true);
    syncInstallItems();
    checkInstalledHere();
    // 첫 화면이 뜨고 잠깐 뒤에 — 떠 있는 카드라 자리를 밀지 않는다
    setTimeout(syncBanner, 1200);
    if (signedOut()) syncAppBadge(0);
    if (!('serviceWorker' in navigator)) return;
    // 탭 틀 안의 탭은 알리지 않는다 — 틀이 한 번 알린다 (spec 2026-09-29 §6.6).
    if (!document.documentElement.dataset.workspaceFrame) watchPwaUpdate(showUpdateToast);
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
    lastUpdate = Date.now();
    document.addEventListener('visibilitychange', checkForUpdate);
}
